import React from 'react';

import type { PageDimensions } from '@aline/common';
import { useReactiveVar } from '@apollo/client/react';
import { equals } from 'ramda';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms, useGetCurrentDoc } from 'src/hooks';
import { HeaderFooterDialog, useEditor } from 'src/layout-engine';

import { CrossReferenceDialog } from './tabs/insert-tab/CrossReferenceDialog';
import { TablePropsDialog } from './tabs/table-tab/TablePropsDialog';
import {
  crossReferenceDialogOpenVar,
  IndentationOptionsDialog,
  PageSetupDialog,
  tablePropertiesDialogOpenVar,
  TrashDialog,
} from '..';
import { ClientTransforms } from '../transforms';

export const ToolbarDialogs = React.memo(function ToolbarDialogs() {
  const editor = useEditor();
  const { document } = useGetCurrentDoc();
  const currentUserId = useCurrentUserId();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const tablePropsDialogOpen = useReactiveVar(tablePropertiesDialogOpenVar);
  const isCrossReferenceDialogOpen = useReactiveVar(crossReferenceDialogOpenVar);

  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const pageDimensions = useEditorTabStateSelector(
    state => state.derived.documentProperties.pageDimensions,
    equals
  );
  const isIndentationDialogOpen = useEditorTabStateSelector(
    state => state.documentUi.indentationDialogOpen
  );
  const isPageSetupDialogOpen = useEditorTabStateSelector(
    state => state.documentUi.pageSetupDialogOpen
  );
  const {
    documentUi: { setIndentationDialogOpen, setPageSetupDialogOpen },
  } = useEditorTabStateActions();

  if (!document) return null;

  return (
    <>
      <IndentationOptionsDialog
        open={isIndentationDialogOpen}
        onClose={(): void => {
          setIndentationDialogOpen(false);
        }}
      />
      <PageSetupDialog
        open={isPageSetupDialogOpen}
        pageDimensions={pageDimensions}
        onChange={(pageDimensions: PageDimensions): void => {
          if (!currentUserId || !editingMode) return;

          AuthorizedTransforms.updatePageDimensions(editor, {
            authorId: currentUserId,
            editingMode,
            newPageDimensions: pageDimensions,
          });
          setPageSetupDialogOpen(false);
          ClientTransforms.focus(editor);
        }}
        onClose={(): void => {
          setPageSetupDialogOpen(false);
        }}
      />
      <TablePropsDialog
        handleClose={() => tablePropertiesDialogOpenVar(false)}
        open={tablePropsDialogOpen}
      />
      <CrossReferenceDialog
        handleClose={() => crossReferenceDialogOpenVar(false)}
        open={isCrossReferenceDialogOpen}
      />
      <HeaderFooterDialog />
      <TrashDialog />
    </>
  );
});
